import { AddressInput } from "@/app/Address/Components/AddressInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { VehicleTypeInput } from "@/app/Vehicles/Components/VehicleTypeInput";
import {
  CreateWorkOrderInput,
  SupplierNotificationSettingType,
  UpdateWorkOrderInput,
} from "@/gql/graphql";
import { useFormikContext } from "formik";
import { SupplierInput } from "@/app/Suppliers/Components/SupplierInput";
import { StepperInput } from "@/lib/Components/Form/Inputs/StepperInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { useTranslations } from "use-intl";
import {
  EmailField,
  useNotificationSettingsFields,
} from "@/app/ProDrivers/WorkOrders/Components/Forms/WorkOrderNotificationSettingForm";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { fetchQuery } from "@/lib/GraphQLCodegen/fetcher";
import { isServiceCenterPickupQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/isServiceCenterPickupQuery";
import { useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { Steps } from "@/lib/Components/Steps/Steps";
import { useFormSteps } from "@/lib/Components/Steps/useFormSteps";
import { Button } from "@/components/catalyst/button";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import { GoogleMapsPlaceDetails } from "@/app/Address/GraphQL/googleMapsAddressLookupQuery";

type AddressCoords = { lat?: number | null; lng?: number | null };

type ServiceCenterDetection = ReturnType<typeof useServiceCenterDetection>;

function useServiceCenterDetection(
  initialSupplierId: string | null | undefined,
) {
  const { initialValues, setFieldValue } = useFormikContext<
    CreateWorkOrderInput | UpdateWorkOrderInput
  >();
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const [isDetected, setIsDetected] = useState(false);
  const isTickedByUser = useRef(!!initialValues.is_service_center);
  const supplierId = useRef(initialSupplierId);
  const pickup = useRef<GoogleMapsPlaceDetails | null>(null);
  const latestCheck = useRef<AbortController | null>(null);

  async function detect(place: GoogleMapsPlaceDetails, signal?: AbortSignal) {
    latestCheck.current?.abort();
    const check = new AbortController();
    latestCheck.current = check;

    if (!supplierId.current) {
      return;
    }

    const isServiceCenter = await fetchQuery(
      isServiceCenterPickupQuery,
      {
        supplierId: supplierId.current,
        lat: place.location.latitude,
        lng: place.location.longitude,
        placeTypes: place.types,
        primaryType: place.primaryType,
      },
      orgId ?? null,
    ).then(
      (data) => data.isServiceCenterPickup,
      () => null,
    );

    if (check.signal.aborted || signal?.aborted) {
      return;
    }

    setIsDetected(isServiceCenter === true);

    if (isServiceCenter !== null && !isTickedByUser.current) {
      await setFieldValue("is_service_center", isServiceCenter);
    }
  }

  return {
    isDetected,
    onPickupSelect(place: GoogleMapsPlaceDetails, signal: AbortSignal) {
      pickup.current = place;
      return detect(place, signal);
    },
    async onSupplierChange(id: string | null) {
      supplierId.current = id;
      latestCheck.current?.abort();

      if (id && pickup.current) {
        await detect(pickup.current);
      }
    },
    onToggle(isServiceCenter: boolean) {
      isTickedByUser.current = isServiceCenter;
    },
  };
}

const stepFields = {
  vehicles: ["supplier.connect", "vehicle_type", "number_of_drivers"],
  trip: ["departureAddress", "deliveryAddress"],
  schedule: ["depart_after", "deliver_before", "description"],
} as const;

function VehicleFields() {
  const t = useTranslations("workOrder.form");

  return (
    <>
      <VehicleTypeInput
        name="vehicle_type"
        label={t("fields.vehicleType")}
        className="col-span-6"
      />
      <StepperInput
        name="number_of_drivers"
        label={t("fields.numberOfDrivers")}
        min={1}
        className="col-span-6"
      />
    </>
  );
}

function TripFields({
  serviceCenter,
  departureCoords,
  deliveryCoords,
  onDepartureCoordsChange,
  onDeliveryCoordsChange,
}: {
  serviceCenter: ServiceCenterDetection;
  departureCoords?: AddressCoords | null;
  deliveryCoords?: AddressCoords | null;
  onDepartureCoordsChange?: (coords: AddressCoords | null) => void;
  onDeliveryCoordsChange?: (coords: AddressCoords | null) => void;
}) {
  const t = useTranslations("workOrder.form");
  const { values } = useFormikContext<
    CreateWorkOrderInput | UpdateWorkOrderInput
  >();

  return (
    <>
      <SwitchInput
        name="has_return_trip"
        label={t("fields.hasReturnTrip")}
        className="col-span-6"
      />
      <SwitchInput
        name="is_service_center"
        label={t("fields.isServiceCenter")}
        tooltip={
          <p className="max-w-xs">{t("fields.isServiceCenterTooltip")}</p>
        }
        onChange={serviceCenter.onToggle}
        className="col-span-6"
      />
      {serviceCenter.isDetected && values.is_service_center ? (
        <WarningBanner className="col-span-full">
          {t("serviceCenterDetected")}
        </WarningBanner>
      ) : null}

      <FormDivider />

      <AddressInput
        name="departureAddress.upsert"
        label={t("fields.pickupAddress")}
        initialLat={departureCoords?.lat}
        initialLng={departureCoords?.lng}
        onCoordsChange={onDepartureCoordsChange}
        onPlaceSelect={serviceCenter.onPickupSelect}
      />
      <AddressInput
        name="deliveryAddress.upsert"
        label={t("fields.deliveryAddress")}
        initialLat={deliveryCoords?.lat}
        initialLng={deliveryCoords?.lng}
        onCoordsChange={onDeliveryCoordsChange}
      />
    </>
  );
}

function ScheduleFields({ autoFocusNotes }: { autoFocusNotes?: boolean }) {
  const t = useTranslations("workOrder.form");

  return (
    <>
      <DateInput
        name="depart_after"
        label={t("fields.departAfter")}
        optionalLabel
        className="col-span-6"
      />
      <DateInput
        name="deliver_before"
        label={t("fields.deliverBefore")}
        optionalLabel
        className="col-span-6"
      />

      <TextAreaInput
        textAreaProps={{
          placeholder: t("fields.placeholder"),
          rows: 4,
        }}
        name="description"
        label={t("fields.notesInstructions")}
        className="col-span-full"
        autoFocus={autoFocusNotes}
      />
    </>
  );
}

export function CreateWorkOrderForm({
  submitLabel,
  autoFocusNotes,
}: {
  submitLabel: string;
  autoFocusNotes?: boolean;
}) {
  const t = useTranslations("workOrder.form");
  const tDialog = useTranslations("common.dialog");
  const { values, initialValues, isSubmitting } =
    useFormikContext<CreateWorkOrderInput>();
  const { close, reject } = useDialogState();
  const {
    stepOrder,
    step,
    stepIndex,
    setStepIndex,
    isLast,
    submitStep,
    goBack,
  } = useFormSteps(stepFields);
  const serviceCenter = useServiceCenterDetection(
    initialValues.supplier?.connect,
  );
  const [departureCoords, setDepartureCoords] = useState<AddressCoords | null>(
    null,
  );
  const [deliveryCoords, setDeliveryCoords] = useState<AddressCoords | null>(
    null,
  );

  const steps = stepOrder.map((id) => ({
    id,
    name: t(`steps.${id}`),
  }));

  return (
    <div className="col-span-full space-y-8">
      <Steps steps={steps} currentIndex={stepIndex} onSelect={setStepIndex} />

      <div className="grid grid-cols-3 gap-6 lg:grid-cols-12">
        {step === "vehicles" ? (
          <>
            {!initialValues.supplier?.connect ? (
              <>
                <SupplierInput
                  name="supplier.connect"
                  label={t("fields.supplier")}
                  className="col-span-6"
                  onChange={(supplier) =>
                    serviceCenter.onSupplierChange(supplier?.id ?? null)
                  }
                />
                {values.supplier?.connect ? <FormDivider /> : null}
              </>
            ) : null}
            {values.supplier?.connect ? <VehicleFields /> : null}
          </>
        ) : null}

        {step === "trip" ? (
          <TripFields
            serviceCenter={serviceCenter}
            departureCoords={departureCoords}
            deliveryCoords={deliveryCoords}
            onDepartureCoordsChange={setDepartureCoords}
            onDeliveryCoordsChange={setDeliveryCoords}
          />
        ) : null}

        {step === "schedule" ? (
          <ScheduleFields autoFocusNotes={autoFocusNotes} />
        ) : null}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 pt-6">
        <Button
          plain
          type="button"
          onClick={() => {
            reject?.(false);
            close();
          }}
        >
          {tDialog("cancel")}
        </Button>
        <div className="flex items-center gap-3">
          {stepIndex > 0 ? (
            <Button outline type="button" onClick={goBack}>
              <ArrowLeftIcon data-slot="icon" />
              {t("steps.back")}
            </Button>
          ) : null}
          {isLast ? (
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              onClick={submitStep}
            >
              <PlusIcon data-slot="icon" />
              {submitLabel}
            </Button>
          ) : (
            <Button type="submit" onClick={submitStep}>
              {t("steps.next")}
              <ArrowRightIcon data-slot="icon" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function UpdateWorkOrderForm({
  supplierId,
  departureCoords,
  deliveryCoords,
}: {
  supplierId: string;
  departureCoords?: AddressCoords;
  deliveryCoords?: AddressCoords;
}) {
  const t = useTranslations("workOrder.form");
  const { toEmails, ccEmails, bccEmails, addEmail, removeEmail } =
    useNotificationSettingsFields();
  const serviceCenter = useServiceCenterDetection(supplierId);

  return (
    <>
      <VehicleFields />
      <TripFields
        serviceCenter={serviceCenter}
        departureCoords={departureCoords}
        deliveryCoords={deliveryCoords}
      />
      <ScheduleFields />

      <FormDivider />

      <h3 className="col-span-full text-sm font-medium text-gray-700">
        {t("sections.notifications")}
      </h3>
      <div className="col-span-full space-y-4">
        <EmailField
          label="To"
          type={SupplierNotificationSettingType.To}
          emails={toEmails}
          onAdd={addEmail}
          onRemove={removeEmail}
        />
        <EmailField
          label="Cc"
          type={SupplierNotificationSettingType.Cc}
          emails={ccEmails}
          onAdd={addEmail}
          onRemove={removeEmail}
        />
        <EmailField
          label="Bcc"
          type={SupplierNotificationSettingType.Bcc}
          emails={bccEmails}
          onAdd={addEmail}
          onRemove={removeEmail}
        />
      </div>
    </>
  );
}
